/**
 * Accessibility Provider Component
 * Wraps application with WCAG 2.1 AA compliance features
 */

import React, { useEffect, useState, createContext, useContext } from 'react';
import { AccessibilityService } from '../../utils/accessibility';
import { logger } from '../../services/loggingService';

interface AccessibilityContextType {
  highContrast: boolean;
  reducedMotion: boolean;
  fontSize: 'normal' | 'large' | 'xlarge';
  toggleHighContrast: () => void;
  setFontSize: (size: 'normal' | 'large' | 'xlarge') => void;
  announceToScreenReader: (message: string) => void;
}

const AccessibilityContext = createContext<AccessibilityContextType>({
  highContrast: false,
  reducedMotion: false,
  fontSize: 'normal',
  toggleHighContrast: () => {},
  setFontSize: () => {},
  announceToScreenReader: () => {},
});

export const useAccessibility = () => useContext(AccessibilityContext);

interface Props {
  children: React.ReactNode;
}

export function AccessibilityProvider({ children }: Props) {
  const [highContrast, setHighContrast] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [fontSize, setFontSizeState] = useState<'normal' | 'large' | 'xlarge'>('normal');

  useEffect(() => {
    // Check user preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const prefersHighContrast = window.matchMedia('(prefers-contrast: high)').matches;
    
    setReducedMotion(prefersReducedMotion);
    setHighContrast(prefersHighContrast);

    // Load saved preferences
    const savedFontSize = localStorage.getItem('accessibility-font-size') as any;
    if (savedFontSize) {
      setFontSizeState(savedFontSize);
    }

    // Add skip link
    AccessibilityService.addSkipLink();

    logger.info('Accessibility features initialized', {
      highContrast: prefersHighContrast,
      reducedMotion: prefersReducedMotion,
    });
  }, []);

  useEffect(() => {
    // Apply high contrast mode
    if (highContrast) {
      document.documentElement.setAttribute('data-high-contrast', 'true');
    } else {
      document.documentElement.removeAttribute('data-high-contrast');
    }
  }, [highContrast]);

  useEffect(() => {
    // Apply reduced motion
    if (reducedMotion) {
      document.documentElement.setAttribute('data-reduced-motion', 'true');
    } else {
      document.documentElement.removeAttribute('data-reduced-motion');
    }
  }, [reducedMotion]);

  useEffect(() => {
    // Apply font size
    document.documentElement.setAttribute('data-font-size', fontSize);
    localStorage.setItem('accessibility-font-size', fontSize);
  }, [fontSize]);

  const toggleHighContrast = () => {
    setHighContrast(prev => !prev);
    AccessibilityService.announceToScreenReader(
      `High contrast mode ${!highContrast ? 'enabled' : 'disabled'}`,
      'polite'
    );
  };

  const setFontSize = (size: 'normal' | 'large' | 'xlarge') => {
    setFontSizeState(size);
    AccessibilityService.announceToScreenReader(
      `Font size changed to ${size}`,
      'polite'
    );
  };

  const announceToScreenReader = (message: string) => {
    AccessibilityService.announceToScreenReader(message, 'polite');
  };

  return (
    <AccessibilityContext.Provider
      value={{
        highContrast,
        reducedMotion,
        fontSize,
        toggleHighContrast,
        setFontSize,
        announceToScreenReader,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
}

